import type { ReactNode } from 'react';
import {
  Box,
  Heading,
  Icon,
  Image,
  InlineStack,
  Badge,
  Link,
  List,
  ListItem,
  Page,
  PageBody,
  Paragraph,
  Strong,
} from '@customerio/pluma-components/react';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import type { WarehouseSync } from '@/services/db/warehouse-sync/entity';
import { ListStatus } from './list-status';
import * as styles from './connector-landing.css';

const DATA_WAREHOUSE_DOCS_URL = 'https://docs.customer.io/data-warehouse-sync';

type ConnectorLandingProps = {
  workspaceId: string;
  sync: WarehouseSync | null;
  /** Whether the workspace's plan puts warehouse sync behind an upgrade. */
  showPremiumLabel: boolean;
  logoSrc: string;
  /** The warehouse's own name, e.g. "Amazon Redshift". */
  title: string;
  blurb: string;
  /** Step 3's body — the only section whose content differs per warehouse. */
  ingestStep: ReactNode;
};

/**
 * The shared frame for the three warehouse connector landing pages. Everything
 * except the logo, name, blurb and the final "ingest the data" step is
 * identical across them.
 */
export function ConnectorLanding({
  workspaceId,
  sync,
  showPremiumLabel,
  logoSrc,
  title,
  blurb,
  ingestStep,
}: ConnectorLandingProps) {
  const taxonomy = useProductTaxonomy();

  return (
    <Page>
      <PageBody>
        <div className={styles.layout}>
          <div>
            <Box className={styles.intro}>
              <Image src={logoSrc} alt="" size="icon-xl" />

              <InlineStack alignBlock="center" gap="100" mt="200">
                <Heading level="3" size="xl">
                  {title}
                </Heading>
                {showPremiumLabel ? (
                  <Badge color="grey" data-test-premium-label>
                    Premium
                  </Badge>
                ) : null}
              </InlineStack>

              <Paragraph mb="200">{blurb}</Paragraph>

              <List styleType="none">
                <ListItem>
                  <InlineStack alignBlock="center" gap="100">
                    <Icon name="check" color="success" />
                    <span>Export message deliveries and metrics</span>
                  </InlineStack>
                </ListItem>
                <ListItem>
                  <InlineStack alignBlock="center" gap="100">
                    <Icon name="check" color="success" />
                    <span>Export {taxonomy.term('campaigns.singular')} workflow journeys</span>
                  </InlineStack>
                </ListItem>
                <ListItem>
                  <InlineStack alignBlock="center" gap="100">
                    <Icon name="check" color="success" />
                    <span>Export {taxonomy.term('people.plural')} and attribute data</span>
                  </InlineStack>
                </ListItem>
              </List>
            </Box>

            <Box className={styles.resources}>
              <Strong display="block" mb="050">
                Get up and running with these resources:
              </Strong>
              <Link href={DATA_WAREHOUSE_DOCS_URL} variant="secondary" isExternal={true}>
                Syncing to a Data Warehouse documentation
              </Link>{' '}
              including getting started, data schema, and FAQs.
            </Box>
          </div>

          <Box className={styles.steps}>
            <Heading level="3" color="bold" mb="100">
              How to automatically export data to {title}
            </Heading>

            <Box mt="200">
              <Heading level="4" color="bold" mb="100">
                1. Create a storage bucket
              </Heading>
              <Paragraph size="xs" color="subtle" mt="050" mb="200">
                Create an Amazon S3, Google Cloud Storage, or Azure Blob Storage bucket with read/write key(s).
              </Paragraph>
            </Box>

            <Box mt="200">
              <Heading level="4" color="bold" mb="100">
                2. Sync Customer.io data to your bucket
              </Heading>
              <Paragraph size="xs" color="subtle" mt="050" mb="200">
                Data will export approximately every 15 minutes in parquet format.
              </Paragraph>

              <ListStatus workspaceId={workspaceId} sync={sync} />
            </Box>

            <Box mt="200">
              <Heading level="4" color="bold" mb="100">
                3. Set up your data warehouse to ingest the data
              </Heading>
              {ingestStep}
            </Box>
          </Box>
        </div>
      </PageBody>
    </Page>
  );
}
